import { PsychrometricCalculator } from './psychrometric';

// Types for chart points and lines
export interface PsychrometricPoint {
  name: string;
  dryBulbTemp: number;
  relativeHumidity?: number;
  humidityRatio?: number;
  enthalpy?: number;
  displayName?: string;
  color: string;
  size?: number;
  labelPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
}

export interface ProcessLine {
  from: string;
  to: string;
  color: string;
  width?: number;
  label?: string;
  type?: 'mixing' | 'cooling' | 'heating' | 'process' | 'bypass';
}

interface ChartDataOptions {
  formData: any;
  ahuCalculationResults: any;
  OUTDOOR_CONDITIONS: any[];
  supplyAirSelectionCriteria?: string;
  outdoorAirSelectionCriteria?: string;
  humidifierType?: string;
  isCoolingMode?: boolean; // 냉방 모드 여부
  raFanType?: 'None' | 'Inside' | 'Outside'; // RA Fan 타입
}

export function generatePsychrometricChartData({
  formData,
  ahuCalculationResults,
  OUTDOOR_CONDITIONS,
  supplyAirSelectionCriteria,
  outdoorAirSelectionCriteria,
  humidifierType,
  isCoolingMode = true, // 기본값은 true (냉방 모드)
  raFanType = 'Inside', // 기본값은 Inside
}: ChartDataOptions): { points: PsychrometricPoint[]; lines: ProcessLine[] } {
  // Helper to safely parse numbers
  const num = (v: any, def = 0) => {
    const n = parseFloat(v);
    return isNaN(n) ? def : n;
  };

  // Get selected outdoor condition
  const selectedCondition = OUTDOOR_CONDITIONS.find(
    (c: any) => c.code === (formData?.outdoorDesignCondition || 'A')
  );

  // Cooling and heating room conditions
  const coolingRoomTemp = num(formData?.coolingRoomTemp, 26);
  const coolingRoomHumidity = num(formData?.coolingRoomHumidity, 55);
  const heatingRoomTemp = num(formData?.heatingRoomTemp, 20);
  const heatingRoomHumidity = num(formData?.heatingRoomHumidity, 40);

  // Outdoor airflows
  // 급기량: 직접입력 모드일 때는 directSupplyAirflow 사용
  let supplyAirflow = num(formData?.supplyAirflow, 25600);
  if (supplyAirSelectionCriteria === 'direct' && formData?.directSupplyAirflow) {
    supplyAirflow = num(formData.directSupplyAirflow, supplyAirflow);
  }
  const outdoorAirflow = num(formData?.directOutdoorAirflow, 8700);

  // Points array
  const points: PsychrometricPoint[] = [];

  // 1. Cooling OA (외기) - 냉방 모드일 때만
  if (isCoolingMode && ahuCalculationResults?.outdoorAir) {
    // 절대습도 계산
    let humidityRatio = ahuCalculationResults.outdoorAir.humidityRatio;
    let enthalpy = ahuCalculationResults.outdoorAir.enthalpy;

    if (humidityRatio === undefined && ahuCalculationResults.outdoorAir.relativeHumidity !== undefined) {
      const state = PsychrometricCalculator.calculate({
        dryBulbTemp: ahuCalculationResults.outdoorAir.dryBulbTemp,
        relativeHumidity: ahuCalculationResults.outdoorAir.relativeHumidity
      });
      if (state) {
        humidityRatio = state.humidityRatio;
      }
    }

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(humidityRatio, 4);
      enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.outdoorAir.dryBulbTemp,
        humidityRatioRounded
      );
      humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: '외기(OA)',
      dryBulbTemp: ahuCalculationResults.outdoorAir.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.outdoorAir.relativeHumidity,
      humidityRatio: humidityRatio,
      enthalpy: enthalpy,
      color: '#FF6B6B',
      size: 6,
    });
  }

  // 2. Heating OA (O2)
  if (ahuCalculationResults?.heatingOutdoorAir) {
    let o2_humidityRatio = ahuCalculationResults.heatingOutdoorAir.humidityRatio;
    let o2_enthalpy = ahuCalculationResults.heatingOutdoorAir.enthalpy;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (o2_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(o2_humidityRatio, 4);
      o2_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.heatingOutdoorAir.dryBulbTemp,
        humidityRatioRounded
      );
      o2_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'o2',
      dryBulbTemp: ahuCalculationResults.heatingOutdoorAir.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.heatingOutdoorAir.relativeHumidity,
      humidityRatio: o2_humidityRatio,
      enthalpy: o2_enthalpy,
      color: '#3F51B5',
      size: 6,
      labelPosition: 'bottom',  // o2는 아래에 표시 (o2-l2 선과 겹침 방지)
    });
  }

  // 3. Cooling RA (환기) and R1 (팬 온도상승 후) - 냉방 모드일 때만
  if (isCoolingMode && ahuCalculationResults?.returnAir) {
    const RAdb = coolingRoomTemp;
    const RArh = coolingRoomHumidity;
    const RA1db = ahuCalculationResults.returnAir.dryBulbTemp;

    // RAte 계산: RA1db - RAdb
    const RAte = RA1db - RAdb;

    if (RAte > 0) {
      // RAte > 0: RA(원본)와 R1(온도상승 후) 두 개의 상태점 표시
      // RA 원본 상태 계산
      const RAstate = PsychrometricCalculator.calculate({
        dryBulbTemp: RAdb,
        relativeHumidity: RArh
      });

      if (RAstate) {
        // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
        const ra_humidityRatioRounded = PsychrometricCalculator.vbaRound(RAstate.humidityRatio, 4);
        const ra_enthalpy = PsychrometricCalculator.calculateEnthalpy(RAdb, ra_humidityRatioRounded);

        points.push({
          name: '환기(RA)',
          dryBulbTemp: RAdb,
          relativeHumidity: RArh,
          humidityRatio: ra_humidityRatioRounded,
          enthalpy: ra_enthalpy,
          color: '#4ECDC4',
          size: 6,
          labelPosition: 'bottom-left',  // RA는 왼쪽 아래
        });
      }

      // R1 (RA1, 온도상승 후) 상태점
      let r1_humidityRatio = ahuCalculationResults.returnAir.humidityRatio;
      let r1_enthalpy = ahuCalculationResults.returnAir.enthalpy;

      // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
      if (r1_humidityRatio !== undefined) {
        const humidityRatioRounded = PsychrometricCalculator.vbaRound(r1_humidityRatio, 4);
        r1_enthalpy = PsychrometricCalculator.calculateEnthalpy(RA1db, humidityRatioRounded);
        r1_humidityRatio = humidityRatioRounded;
      }

      const r1Point = {
        name: 'r1',
        displayName: 'R1',
        dryBulbTemp: RA1db,
        relativeHumidity: ahuCalculationResults.returnAir.relativeHumidity,
        humidityRatio: r1_humidityRatio,
        enthalpy: r1_enthalpy,
        color: '#26A69A',
        size: 6,
        labelPosition: 'right',  // R1은 오른쪽
      };
      points.push(r1Point);
      // console.log('✅ [psychroChartData] R1 생성:', { temp: RA1db, rh: ahuCalculationResults.returnAir.relativeHumidity });
    } else {
      // RAte = 0: RA만 표시
      let ra_only_humidityRatio = ahuCalculationResults.returnAir.humidityRatio;
      let ra_only_enthalpy = ahuCalculationResults.returnAir.enthalpy;

      // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
      if (ra_only_humidityRatio !== undefined) {
        const humidityRatioRounded = PsychrometricCalculator.vbaRound(ra_only_humidityRatio, 4);
        ra_only_enthalpy = PsychrometricCalculator.calculateEnthalpy(
          ahuCalculationResults.returnAir.dryBulbTemp,
          humidityRatioRounded
        );
        ra_only_humidityRatio = humidityRatioRounded;
      }

      points.push({
        name: '환기(RA)',
        dryBulbTemp: ahuCalculationResults.returnAir.dryBulbTemp,
        relativeHumidity: ahuCalculationResults.returnAir.relativeHumidity,
        humidityRatio: ra_only_humidityRatio,
        enthalpy: ra_only_enthalpy,
        color: '#4ECDC4',
        size: 6,
        labelPosition: 'bottom-right',
      });
    }
  }

  // 4. Heating RA (R2) - RA Fan이 None이 아닐 때만 (환기 혼합 시스템)
  if (raFanType !== 'None' && ahuCalculationResults?.heatingReturnAir) {
    let r2_humidityRatio = ahuCalculationResults.heatingReturnAir.humidityRatio;
    let r2_enthalpy = ahuCalculationResults.heatingReturnAir.enthalpy;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (r2_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(r2_humidityRatio, 4);
      r2_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.heatingReturnAir.dryBulbTemp,
        humidityRatioRounded
      );
      r2_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'r2',
      dryBulbTemp: ahuCalculationResults.heatingReturnAir.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.heatingReturnAir.relativeHumidity,
      humidityRatio: r2_humidityRatio,
      enthalpy: r2_enthalpy,
      color: '#9C27B0',
      size: 6,
      labelPosition: 'bottom-right',
    });
  }

  // 5. Cooling MA (혼합공기) - 냉방 모드일 때만
  if (isCoolingMode && ahuCalculationResults?.mixedAir) {
    // ahuCalculations.ts의 calculateMixedAir 함수에서 이미 올바르게 계산된 값을 사용
    // calculateMixedAir는 다음 순서로 계산:
    // 1. Airr = OAvol / SAvol
    // 2. OA.x, RA.x를 vbaRound(4자리)
    // 3. MA.tdb = Airr × OA.tdb + (1-Airr) × RA.tdb
    // 4. MA.x = Airr × vbaRound(OA.x,4) + (1-Airr) × vbaRound(RA.x,4)
    // 5. MA.h = entfuc(MA.tdb, vbaRound(MA.x, 4))
    // 6. MA.RH = rhfuc(MA.tdb, MA.x, elevation) ← 반올림 안함

    let ma_humidityRatio = ahuCalculationResults.mixedAir.humidityRatio;
    let ma_enthalpy = ahuCalculationResults.mixedAir.enthalpy;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    // (calculateMixedAir에서 이미 계산되었지만, 일관성을 위해 재확인)
    if (ma_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(ma_humidityRatio, 4);
      ma_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.mixedAir.dryBulbTemp,
        humidityRatioRounded
      );
      ma_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'ma',
      displayName: '혼합(MA)',
      dryBulbTemp: ahuCalculationResults.mixedAir.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.mixedAir.relativeHumidity,
      humidityRatio: ma_humidityRatio,
      enthalpy: ma_enthalpy,
      color: '#45B7D1',
      size: 6,
      labelPosition: 'bottom-right',
    });
  }

  // 6. Heating MA (m1) - RA Fan이 None이 아닐 때만 (환기 혼합)
  // RA Fan이 None이면 전량 외기이므로 혼합공기 없음
  if (raFanType !== 'None' && ahuCalculationResults?.heatingMixedAir) {
    let m1_humidityRatio = ahuCalculationResults.heatingMixedAir.humidityRatio;
    let m1_enthalpy = ahuCalculationResults.heatingMixedAir.enthalpy;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (m1_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(m1_humidityRatio, 4);
      m1_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.heatingMixedAir.dryBulbTemp,
        humidityRatioRounded
      );
      m1_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'm1',
      dryBulbTemp: ahuCalculationResults.heatingMixedAir.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.heatingMixedAir.relativeHumidity,
      humidityRatio: m1_humidityRatio,
      enthalpy: m1_enthalpy,
      color: '#673AB7',
      size: 6,
      labelPosition: 'bottom-right',
    });
  }

  // 7. Cooling L1 (냉각코일 후) - 냉방 모드일 때만
  if (isCoolingMode && ahuCalculationResults?.supplyAir) {
    let l1_humidityRatio = ahuCalculationResults.supplyAir.humidityRatio;
    let l1_enthalpy = undefined;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (l1_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(l1_humidityRatio, 4);
      l1_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.supplyAir.dryBulbTemp,
        humidityRatioRounded
      );
      l1_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'l1',
      displayName: '냉방코일출구(LA)',
      dryBulbTemp: ahuCalculationResults.supplyAir.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.supplyAir.relativeHumidity,
      humidityRatio: l1_humidityRatio,
      enthalpy: l1_enthalpy,
      color: '#96CEB4',
      size: 6,
      labelPosition: 'bottom-right',
    });
  }

  // 8. Heating L2 (난방 급기)
  if (ahuCalculationResults?.heatingSupplyAir) {
    let l2_humidityRatio = ahuCalculationResults.heatingSupplyAir.humidityRatio;
    let l2_enthalpy = ahuCalculationResults.heatingSupplyAir.enthalpy;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (l2_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(l2_humidityRatio, 4);
      l2_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.heatingSupplyAir.dryBulbTemp,
        humidityRatioRounded
      );
      l2_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'l2',
      dryBulbTemp: ahuCalculationResults.heatingSupplyAir.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.heatingSupplyAir.relativeHumidity,
      humidityRatio: l2_humidityRatio,
      enthalpy: l2_enthalpy,
      color: '#FF9800',
      size: 6,
    });
  }

  // 9. Heating L3 (난방 가열 후)
  if (ahuCalculationResults?.heatingAfterHeating) {
    let l3_humidityRatio = ahuCalculationResults.heatingAfterHeating.humidityRatio;
    let l3_enthalpy = ahuCalculationResults.heatingAfterHeating.enthalpy;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (l3_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(l3_humidityRatio, 4);
      l3_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.heatingAfterHeating.dryBulbTemp,
        humidityRatioRounded
      );
      l3_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'l3',
      dryBulbTemp: ahuCalculationResults.heatingAfterHeating.dryBulbTemp,
      relativeHumidity: ahuCalculationResults.heatingAfterHeating.relativeHumidity,
      humidityRatio: l3_humidityRatio,
      enthalpy: l3_enthalpy,
      color: '#4CAF50',
      size: 6,
      labelPosition: 'top-right',  // l3는 위쪽으로 이동 (상태점과 가깝게)
    });
  }

  // 10. Cooling SA (급기) - 냉방 모드일 때만
  if (isCoolingMode && ahuCalculationResults?.supplyAir) {
    const saTempRise = ahuCalculationResults.supplyFanTempRise || 0;
    const rhTempRise = num(formData?.rhReheatTemp, 0);
    const totalTempRise = saTempRise + rhTempRise;

    let sa_humidityRatio = ahuCalculationResults.supplyAir.humidityRatio;
    let sa_enthalpy = undefined;

    // VBA 방식: 절대습도를 4자리 반올림 후 엔탈피 재계산
    if (sa_humidityRatio !== undefined) {
      const humidityRatioRounded = PsychrometricCalculator.vbaRound(sa_humidityRatio, 4);
      sa_enthalpy = PsychrometricCalculator.calculateEnthalpy(
        ahuCalculationResults.supplyAir.dryBulbTemp + totalTempRise,
        humidityRatioRounded
      );
      sa_humidityRatio = humidityRatioRounded;
    }

    points.push({
      name: 'la',
      displayName: 'la',
      dryBulbTemp: ahuCalculationResults.supplyAir.dryBulbTemp + totalTempRise,
      relativeHumidity: ahuCalculationResults.supplyAir.relativeHumidity,
      humidityRatio: sa_humidityRatio,
      enthalpy: sa_enthalpy,
      color: '#81C784',
      size: 6,
      labelPosition: 'bottom-right',
    });
  }

  // 11. ADP (풍량 선정 탭과 동일한 조건) - 냉방 모드일 때만
  if (isCoolingMode && ahuCalculationResults?.adp) {
    points.push({
      name: 'adp',
      displayName: `adp${!ahuCalculationResults.adp.valid ? ' (계산불가!)' : ''}`,
      dryBulbTemp: ahuCalculationResults.adp.temperature,
      humidityRatio: ahuCalculationResults.adp.humidityRatio,
      relativeHumidity: ahuCalculationResults.adp.relativeHumidity,
      color: ahuCalculationResults.adp.valid ? '#FECA57' : '#FF3838',
      size: 6,
      labelPosition: 'bottom-right',
    });
  }

  // 실제 생성된 점들만을 기반으로 라인 생성
  const pointNames = points.map(p => p.name);

  // 생성된 점들 확인
  const hasADP = pointNames.some(name => name.includes('adp') || name.includes('ADP'));
  const hasL1 = pointNames.includes('l1');

  // Lines array - 냉방(파란색), 난방(빨간색) 색상 완전 통일
  const COOLING_COLOR = '#2196F3'; // 파란색 (냉방 전용)
  const HEATING_COLOR = '#F44336'; // 빨간색 (난방 전용)

  const lines: ProcessLine[] = [];

  // 냉방 프로세스 라인 구성
  // 혼합공기 → 외기 (ma → OA)
  lines.push(
    { from: 'ma', to: '외기(OA)', color: COOLING_COLOR, width: 2.5, type: 'mixing' }
  );

  // 냉각후 → 혼합공기 (l1 → ma)
  lines.push(
    { from: 'l1', to: 'ma', color: COOLING_COLOR, width: 2.5, type: 'cooling' }
  );

  // 냉각코일 후 → 급기 (l1 → la)
  lines.push(
    { from: 'l1', to: 'la', color: COOLING_COLOR, width: 2.5, type: 'process' }
  );

  // R1이 있으면: la → RA → r1 → ma
  // R1이 없으면: la → RA → ma
  if (pointNames.includes('r1')) {
    lines.push(
      // 급기 → 환기 (la → RA)
      { from: 'la', to: '환기(RA)', color: COOLING_COLOR, width: 2.5, type: 'process' },
      // 환기 → 실내 (RA → r1)
      { from: '환기(RA)', to: 'r1', color: COOLING_COLOR, width: 2.5, type: 'process' },
      // 실내 → 혼합공기 (r1 → ma)
      { from: 'r1', to: 'ma', color: COOLING_COLOR, width: 2.5, type: 'mixing' }
    );
  } else {
    lines.push(
      // 급기 → 환기 (la → RA)
      { from: 'la', to: '환기(RA)', color: COOLING_COLOR, width: 2.5, type: 'process' },
      // 환기 → 혼합공기 (RA → ma)
      { from: '환기(RA)', to: 'ma', color: COOLING_COLOR, width: 2.5, type: 'mixing' }
    );
  }

  // ADP가 있을 때 ADP → L1 냉각 선 추가 (valid 여부와 관계없이)
  if (hasADP && hasL1) {
    lines.push(
      { from: 'adp', to: 'l1', color: COOLING_COLOR, width: 2.5, type: 'cooling' }
    );
  }

  // 난방 라인들 추가 (모두 동일한 빨간색)
  if (selectedCondition && supplyAirflow > 0) {
    if (raFanType !== 'None') {
      // RA Fan이 있는 경우: 환기 혼합 프로세스
      const heatingPoints = ['o2', 'r2', 'm1', 'l2', 'l3'];
      const hasAllHeatingPoints = heatingPoints.every(pointName =>
        points.find(p => p.name === pointName)
      );

      if (hasAllHeatingPoints) {
        lines.push(
          // 난방용 외기 → 난방용 혼합공기
          { from: 'o2', to: 'm1', color: HEATING_COLOR, width: 2.5, type: 'heating' },
          // 난방용 환기 → 난방용 혼합공기
          { from: 'r2', to: 'm1', color: HEATING_COLOR, width: 2.5, type: 'heating' },
          // 난방용 혼합공기 → 가열 후 (l3)
          { from: 'm1', to: 'l3', color: HEATING_COLOR, width: 2.5, type: 'heating' },
          // 가열 후 → 가습 후 (l2)
          { from: 'l3', to: 'l2', color: HEATING_COLOR, width: 2.5, type: 'process' },
          // 난방용 급기 → 난방용 환기
          { from: 'l2', to: 'r2', color: HEATING_COLOR, width: 2.5, type: 'process' }
        );
      }
    } else {
      // RA Fan이 None인 경우: 전량 외기 프로세스 (o2 → l3 → l2, 환기 버림)
      const heatingPoints = ['o2', 'l2', 'l3'];
      const hasAllHeatingPoints = heatingPoints.every(pointName =>
        points.find(p => p.name === pointName)
      );

      if (hasAllHeatingPoints) {
        lines.push(
          // 외기 → 급기 (전체 프로세스 직선)
          { from: 'o2', to: 'l2', color: HEATING_COLOR, width: 2.5, type: 'process' },
          // 외기 → 가열 후 (o2 → l3)
          { from: 'o2', to: 'l3', color: HEATING_COLOR, width: 2.5, type: 'heating' },
          // 가열 후 → 가습 후 (l3 → l2)
          { from: 'l3', to: 'l2', color: HEATING_COLOR, width: 2.5, type: 'process' }
        );
      }
    }
  }

  // console.log('🎯 [psychroChartData] 반환 전 r1 포함:', points.some(p => p.name === 'r1'));
  return { points, lines };
}